import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Grid from '@mui/material/Grid'
import * as Yup from 'yup'
import {
  DeleteOutline,
  EditOutlined,
  CancelOutlined,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  CheckCircleOutline as CheckIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Schedule as PendingIcon,
} from '@mui/icons-material'
import Button from '@/v2/components/common/Button'
import IconButton from '@/v2/components/common/IconButton'
import Typography from '@/v2/components/common/Typography'
import TextField from '@/v2/components/common/TextField'
import Box from '@mui/material/Box'
import SelectField from '@/v2/components/common/SelectField'
import { imageOrientations, imageTypeCode } from '../../utils'
import {
  useRemoveClaimAttachment,
  useCreateClaimAttachment,
  useCreateClaimAttachmentImages,
  useRemoveClaimAttachmentImages,
  useUpdateClaimAttachment,
  useUpdateClaimAttachmentImages,
} from '@/v2/hooks/insurance/claim-subforms/useClaimAttachments'
import { CircularProgress } from '@mui/material'
import Divider from '@/v2/components/common/Divider'
import { useTheme } from '@mui/material/styles'
import Dialog from '@mui/material/Dialog'

const attachmentSchema = Yup.object().shape({
  narrative: Yup.string().required('Narrative is required'),
})

const fileSchema = Yup.object().shape({
  date: Yup.string().required('Date is required'),
  typeCode: Yup.string().required('File type is required'),
  orientationType: Yup.string().required('Orientation is required'),
})

const FileRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  margin: 4px 0;
  border: none;
  border-radius: 0;
  background-color: transparent;

  @media (min-width: 600px) {
    flex-direction: row;
    align-items: center;
  }

  &:hover {
    background-color: ${props => props.theme.palette.grey[50]};
  }
`

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  @media (min-width: 600px) {
    flex: 0 0 200px;
    width: auto;
  }
`

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${props => {
    switch (props.status) {
      case 'sync_completed':
        return props.theme.palette.success.main
      case 'sync_failed':
        return props.theme.palette.error.main
      case 'pending':
        return props.theme.palette.warning.main
      default:
        return props.theme.palette.text.secondary
    }
  }};
`

const FileName = styled.div`
  font-weight: 500;
  color: ${props => props.theme.palette.text.primary};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  max-width: 100%;

  @media (min-width: 600px) {
    width: 160px;
    max-width: 160px;
    min-width: 80px;
    flex: none;
  }
`

const Attachments = ({
  values = {},
  onChange,
  claimId,
  viewOnly = false,
  status,
}) => {
  const theme = useTheme()
  const [attachments, setAttachments] = useState(values?.attachments || [])
  const [editingAttachmentId, setEditingAttachmentId] = useState(null)
  const [isAdding, setIsAdding] = useState(false)
  const [initialAttachmentData, setInitialAttachmentData] = useState({
    narrative: '',
    attachments: [],
  })
  const [validationErrors, setValidationErrors] = useState({})
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [attachmentToDelete, setAttachmentToDelete] = useState(null)
  const [showNarrativeConfirmation, setShowNarrativeConfirmation] = useState(
    false
  )
  const [currentStep, setCurrentStep] = useState(1)
  const [savedAttachmentId, setSavedAttachmentId] = useState(null)
  const [globalUploadingFiles, setGlobalUploadingFiles] = useState(new Set())

  const {
    removeClaimAttachment,
    isLoading: isDeletingAttachment,
  } = useRemoveClaimAttachment(claimId)
  const {
    createClaimAttachment,
    isLoading: isCreatingAttachment,
  } = useCreateClaimAttachment(claimId)
  const {
    updateClaimAttachment,
    isLoading: isUpdatingAttachment,
  } = useUpdateClaimAttachment(claimId)

  useEffect(() => {
    if (values.attachments) setAttachments(values?.attachments || [])
  }, [values])

  const handleDeleteAttachmentSet = async attachmentId => {
    if (isDeletingAttachment) return // Prevent multiple requests
    try {
      await removeClaimAttachment(attachmentId)
      const updatedAttachments = attachments.filter(
        ({ id }) => id !== attachmentId
      )
      setAttachments(updatedAttachments)
      onChange?.({ ...values, attachments: updatedAttachments })
    } catch (error) {
      console.error('Error deleting attachment:', error)
    }
  }

  const handleAddAttachmentSet = () => {
    setIsAdding(true)
    setEditingAttachmentId(null)
    setInitialAttachmentData({ narrative: '', attachments: [] })
    setValidationErrors({})
    setCurrentStep(1)
    setSavedAttachmentId(null)
  }

  const handleSaveAttachmentSet = async () => {
    try {
      if (isCreatingAttachment || isUpdatingAttachment) return

      await attachmentSchema.validate(initialAttachmentData, {
        abortEarly: false,
      })
      setValidationErrors({})

      if (isAdding) {
        const formData = new FormData()
        formData.append(
          'attachment[narrative]',
          initialAttachmentData.narrative
        )
        initialAttachmentData?.images?.forEach(file => {
          formData.append(
            'attachment[images_attributes][][orientation_type]',
            file.orientationType
          )
          formData.append(
            'attachment[images_attributes][][type_code]',
            file.typeCode
          )
          formData.append('attachment[images_attributes][][date]', file.date)
          formData.append('attachment[images_attributes][][file]', file.file)
        })
        const res = await createClaimAttachment(formData)
        if (!res.error) {
          const newAttachment = { ...initialAttachmentData, id: res.id }
          const newAttachments = [...attachments, newAttachment]
          setAttachments(newAttachments)
          onChange?.({ ...values, attachments: newAttachments })
          setIsAdding(false)
          // Don't clear initialAttachmentData when moving to step 2
          return { success: true, attachmentId: res.id }
        }
        return { success: false, error: res.error }
      }

      if (editingAttachmentId) {
        const data = {
          body: {
            attachment: {
              narrative: initialAttachmentData.narrative,
            },
          },
          attachmentId: editingAttachmentId,
        }
        const res = await updateClaimAttachment(data)
        if (!res.error) {
          const updatedAttachments = attachments.map(attachment =>
            attachment.id === editingAttachmentId
              ? { ...attachment, narrative: initialAttachmentData.narrative }
              : attachment
          )
          setAttachments(updatedAttachments)
          onChange?.({ ...values, attachments: updatedAttachments })
          setEditingAttachmentId(null)
          setInitialAttachmentData({ narrative: '', attachments: [] })
          return { success: true, attachmentId: editingAttachmentId }
        }
        return { success: false, error: res.error }
      }
    } catch (validationErrors) {
      const errors = {}
      validationErrors.inner.forEach(err => {
        errors[
          `${isAdding ? 'newAttachment' : editingAttachmentId}.${err.path}`
        ] = err.message
      })
      setValidationErrors(errors)
      return { success: false, error: errors }
    }
  }

  const handleEditAttachmentSet = attachmentId => {
    setEditingAttachmentId(attachmentId)
    setIsAdding(false)
    const attachmentToEdit = attachments.find(set => set.id === attachmentId)
    setInitialAttachmentData(attachmentToEdit)
    setValidationErrors({})
  }

  const handleCancelEdit = () => {
    setEditingAttachmentId(null)
    setIsAdding(false)
    setInitialAttachmentData({ narrative: '', attachments: [] })
    setValidationErrors({})
    setCurrentStep(1)
    setSavedAttachmentId(null)
  }

  const handleRequestDeleteAttachment = attachmentId => {
    setAttachmentToDelete(attachmentId)
    setShowDeleteConfirmation(true)
  }

  const handleConfirmDeleteAttachment = async () => {
    if (attachmentToDelete) {
      await handleDeleteAttachmentSet(attachmentToDelete)
      setAttachmentToDelete(null)
      setShowDeleteConfirmation(false)
    }
  }

  const handleCancelDeleteAttachment = () => {
    setAttachmentToDelete(null)
    setShowDeleteConfirmation(false)
  }

  const handleRequestSaveNarrative = () => {
    setShowNarrativeConfirmation(true)
  }

  const handleConfirmSaveNarrative = async () => {
    setShowNarrativeConfirmation(false)
    const result = await handleSaveAttachmentSet()
    if (result && result.success) {
      setCurrentStep(2)
      setSavedAttachmentId(result.attachmentId)
      setEditingAttachmentId(result.attachmentId)
    }
  }

  const handleCancelSaveNarrative = () => {
    setShowNarrativeConfirmation(false)
  }

  return (
    <>
      <Grid item xs={12} sx={{ marginTop: '12px' }}>
        <Typography variant="h5" bold fontSize="16px">
          Attachments
        </Typography>
        <Divider margin="5px 0px" />
      </Grid>

      {/* Existing Attachments */}
      {attachments.map((attachmentSet, index) =>
        attachmentSet && attachmentSet.id ? (
          <Grid item key={attachmentSet.id} xs={12}>
            <Box
              sx={{
                border: 'none',
                borderRadius: 0,
                p: 0,
              }}
            >
              <AttachmentItem
                theme={theme}
                isEdit={editingAttachmentId === attachmentSet.id}
                showLabel={true}
                values={
                  editingAttachmentId === attachmentSet.id
                    ? initialAttachmentData
                    : attachmentSet
                }
                errorAffixe={
                  editingAttachmentId === attachmentSet.id
                    ? editingAttachmentId
                    : `attachmentSet[${index}]`
                }
                error={validationErrors}
                onChange={value => setInitialAttachmentData(value)}
                allowFileEdit={editingAttachmentId === attachmentSet.id}
                attachmentId={attachmentSet.id}
                claimId={claimId}
                currentStep={currentStep}
                savedAttachmentId={savedAttachmentId}
                globalUploadingFiles={globalUploadingFiles}
                setGlobalUploadingFiles={setGlobalUploadingFiles}
                onCancelAttachmentSet={handleCancelEdit}
                viewOnly={viewOnly}
              />
              {editingAttachmentId === attachmentSet.id ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    mt: 1,
                    ml: 2,
                  }}
                >
                  <Button
                    variant="text"
                    disableElevation
                    startIcon={<CancelOutlined color="error" />}
                    onClick={handleCancelEdit}
                    sx={{
                      marginBottom: '4px',
                      color: '#000',
                      minWidth: 0,
                      '&:hover': {
                        backgroundColor: theme.palette.silver.dark,
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              ) : (
                !viewOnly && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 1,
                      mt: 1,
                      ml: 2,
                    }}
                  >
                    <Button
                      variant="text"
                      disableElevation
                      startIcon={<EditOutlined color="link" />}
                      onClick={() => handleEditAttachmentSet(attachmentSet.id)}
                      sx={{
                        marginBottom: '4px',
                        color: '#000',
                        minWidth: 0,
                        '&:hover': {
                          backgroundColor: theme.palette.silver.dark,
                        },
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="text"
                      disableElevation
                      startIcon={<DeleteOutline color="link" />}
                      onClick={() =>
                        handleRequestDeleteAttachment(attachmentSet.id)
                      }
                      sx={{
                        marginBottom: '4px',
                        color: '#000',
                        minWidth: 0,
                        '&:hover': {
                          backgroundColor: theme.palette.silver.dark,
                        },
                      }}
                      disabled={
                        isDeletingAttachment || globalUploadingFiles.size > 0
                      }
                    >
                      Delete
                    </Button>
                  </Box>
                )
              )}
            </Box>
          </Grid>
        ) : null
      )}

      {/* Add New Attachment Button */}
      {attachments.length === 0 &&
        !viewOnly &&
        !isAdding &&
        editingAttachmentId === null && (
          <Grid item xs={12} sx={{ marginTop: '12px', pl: '0 !important' }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddAttachmentSet}
              disabled={isCreatingAttachment || isUpdatingAttachment}
              sx={{
                py: 1.5,
                borderStyle: 'dashed',
                borderWidth: 2,
                color: theme.palette.text.secondary,
                '&:hover': {
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  backgroundColor: theme.palette.grey[50],
                },
              }}
            >
              Add Attachment
            </Button>
          </Grid>
        )}

      {isAdding && (
        <Grid item xs={12}>
          <div>
            <Typography
              variant="h6"
              sx={{ mb: 1, color: theme.palette.secondary.main }}
            >
              New Attachment
            </Typography>
            <AttachmentItem
              theme={theme}
              values={initialAttachmentData}
              errorAffixe="newAttachment"
              error={validationErrors}
              isEdit={true}
              onChange={newData => setInitialAttachmentData(newData)}
              allowFileEdit={currentStep === 2 || isAdding}
              isAdding={isAdding}
              claimId={claimId}
              currentStep={currentStep}
              savedAttachmentId={savedAttachmentId}
              globalUploadingFiles={globalUploadingFiles}
              setGlobalUploadingFiles={setGlobalUploadingFiles}
              onCancelAttachmentSet={handleCancelEdit}
              viewOnly={viewOnly}
            />
            {currentStep === 1 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 1,
                  mt: 1,
                  ml: 2,
                }}
              >
                <Button
                  variant="text"
                  disableElevation
                  startIcon={
                    isCreatingAttachment ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <CheckIcon color="link" />
                    )
                  }
                  onClick={handleRequestSaveNarrative}
                  disabled={isCreatingAttachment}
                  sx={{
                    marginBottom: '4px',
                    color: '#000',
                    minWidth: 0,
                    '&:hover': {
                      backgroundColor: theme.palette.silver.dark,
                    },
                  }}
                >
                  {isCreatingAttachment ? 'Saving...' : 'Next'}
                </Button>
                <Button
                  variant="text"
                  disableElevation
                  startIcon={<CancelOutlined color="error" />}
                  onClick={handleCancelEdit}
                  disabled={globalUploadingFiles.size > 0}
                  sx={{
                    marginBottom: '4px',
                    color: '#000',
                    minWidth: 0,
                    '&:hover': {
                      backgroundColor: theme.palette.silver.dark,
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </div>
        </Grid>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={showDeleteConfirmation}
        onClose={handleCancelDeleteAttachment}
        fullWidth
        maxWidth="xs"
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ marginBottom: '14px' }}>
            Are you sure you want to delete this attachment? This action cannot
            be undone.
          </Typography>
          <Box
            display="flex"
            width={'100%'}
            justifyContent={'flex-end'}
            gap={1}
          >
            <Button
              variant="text"
              color="secondary"
              onClick={handleCancelDeleteAttachment}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDeleteAttachment}
              sx={{ color: '#fff' }}
              disabled={isDeletingAttachment}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Narrative confirmation dialog */}
      <Dialog
        open={showNarrativeConfirmation}
        onClose={handleCancelSaveNarrative}
        fullWidth
        maxWidth="sm"
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ marginBottom: '14px' }}>
            Confirm Narrative
          </Typography>
          <Typography
            variant="body2"
            sx={{ marginBottom: '16px', color: theme.palette.text.secondary }}
          >
            Please review your narrative below. Once saved, the narrative cannot
            be changed later.
          </Typography>
          <Box
            sx={{
              p: 2,
              mb: 2,
              border: 'none',
              borderRadius: 0,
              backgroundColor: theme.palette.grey[50],
              minHeight: 80,
            }}
          >
            <Typography variant="body1">
              {initialAttachmentData.narrative || 'No narrative provided'}
            </Typography>
          </Box>
          <Box
            display="flex"
            width={'100%'}
            justifyContent={'flex-end'}
            gap={1}
          >
            <Button
              variant="text"
              color="secondary"
              onClick={handleCancelSaveNarrative}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleConfirmSaveNarrative}
              sx={{ color: '#fff' }}
              disabled={isCreatingAttachment || isUpdatingAttachment}
            >
              Save Narrative
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  )
}

const AttachmentItem = ({
  isEdit = false,
  isAdding = false,
  values = {},
  error = {},
  errorAffixe,
  onChange,
  allowFileEdit = false,
  attachmentId,
  claimId,
  theme,
  currentStep,
  savedAttachmentId,
  globalUploadingFiles,
  setGlobalUploadingFiles,
  onCancelAttachmentSet,
  viewOnly = false,
}) => {
  const [editingFileIndex, setEditingFileIndex] = useState(null)
  const [addingFileIndex, setAddingFileIndex] = useState(null)
  const [allAttachments, setAllAttachments] = useState(values?.images || [])
  const [showFileDeleteConfirmation, setShowFileDeleteConfirmation] = useState(
    false
  )
  const [fileToDelete, setFileToDelete] = useState(null)
  const [fileValidationErrors, setFileValidationErrors] = useState({})
  const [updatingImageIndex, setUpdatingImageIndex] = useState(null)
  const {
    createClaimAttachmentImages,
    isLoading: isLoadingCreate,
  } = useCreateClaimAttachmentImages(savedAttachmentId || attachmentId, claimId)
  const {
    updateClaimAttachmentImages,
    isLoading: isUpdatingImage,
  } = useUpdateClaimAttachmentImages(savedAttachmentId || attachmentId, claimId)
  const {
    removeClaimAttachmentImage,
    isLoading: isDeletingImage,
  } = useRemoveClaimAttachmentImages(savedAttachmentId || attachmentId, claimId)

  useEffect(() => {
    setAllAttachments(values?.images || [])
  }, [values.images])

  const handleFileAdd = async file => {
    const newFileIndex = allAttachments?.length

    const newFile = {
      id: Date.now(),
      isNew: true,
      file,
      name: file.name,
      date: new Date().toISOString().split('T')[0],
      typeCode: 'RB',
      orientationType: 'LEFT',
      pendingUpload: true, // Mark as pending upload
    }

    // Add file to local state immediately for UI feedback
    const updatedFiles = [...allAttachments, newFile]
    setAllAttachments(updatedFiles)
    onChange({ ...values, images: updatedFiles })
    setAddingFileIndex(newFileIndex)
  }

  const handleConfirmFileUpload = async index => {
    const file = allAttachments[index]
    if (!file || !file.pendingUpload) return

    setAddingFileIndex(index)
    setGlobalUploadingFiles(prev => new Set(prev).add(index))

    try {
      const formData = new FormData()
      formData.append('image[type_code]', file.typeCode)
      formData.append('image[orientation_type]', file.orientationType)
      formData.append('image[date]', file.date)
      formData.append('image[file]', file.file)

      const res = await createClaimAttachmentImages(formData)

      if (!res.error && res.id) {
        const finalUpdatedFiles = allAttachments.map((file, i) =>
          i === index
            ? { ...file, id: res.id, isNew: false, pendingUpload: false }
            : file
        )
        setAllAttachments(finalUpdatedFiles)
        onChange?.({ ...values, images: finalUpdatedFiles })
        setAddingFileIndex(null)

        // Always reset to listing state after successful file upload
        // This ensures we return to the initial listing state with Edit/Delete buttons
        onCancelAttachmentSet()
      } else {
        // If save failed, remove the file from the list
        const filesWithoutFailed = allAttachments.filter((_, i) => i !== index)
        setAllAttachments(filesWithoutFailed)
        onChange({ ...values, images: filesWithoutFailed })
        setAddingFileIndex(null)
        console.error('Failed to save file:', res.error)
      }
    } catch (error) {
      // If save failed, remove the file from the list
      const filesWithoutFailed = allAttachments.filter((_, i) => i !== index)
      setAllAttachments(filesWithoutFailed)
      onChange({ ...values, images: filesWithoutFailed })
      setAddingFileIndex(null)
      console.error('Error saving file:', error)
    } finally {
      // Clear uploading state
      setGlobalUploadingFiles(prev => {
        const newSet = new Set(prev)
        newSet.delete(index)
        return newSet
      })
      onCancelAttachmentSet()
    }
  }

  const handleFileChange = async (index, field, value) => {
    const updatedFiles = allAttachments.map((file, i) =>
      i === index ? { ...file, [field]: value } : file
    )
    setAllAttachments(updatedFiles)
    onChange({ ...values, images: updatedFiles })

    // Clear validation error for this field when user starts typing
    if (fileValidationErrors[`file${index}.${field}`]) {
      setFileValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[`file${index}.${field}`]
        return newErrors
      })
    }
  }

  const handleFileRemove = async index => {
    const file = allAttachments[index]
    if (file.isNew) {
      const updatedFiles = allAttachments.filter((_, i) => i !== index)
      setAllAttachments(updatedFiles)
      setAddingFileIndex(null)
      onChange({ ...values, images: updatedFiles })
    } else {
      const res = await removeClaimAttachmentImage(file.id)
      if (!res.error) {
        const updatedFiles = allAttachments.filter((_, i) => i !== index)
        setAllAttachments(updatedFiles)
        onChange({ ...values, images: updatedFiles })
        onCancelAttachmentSet()
      }
    }
  }

  const handleSaveFile = async index => {
    const file = allAttachments[index]
    if (!file.isNew && file.id) {
      try {
        setUpdatingImageIndex(index)
        // Validate file data before saving
        await fileSchema.validate(file, { abortEarly: false })

        // Clear validation errors on successful validation
        setFileValidationErrors(prev => {
          const newErrors = { ...prev }
          delete newErrors[`file${index}.date`]
          delete newErrors[`file${index}.typeCode`]
          delete newErrors[`file${index}.orientationType`]
          return newErrors
        })

        const body = {
          imageId: file.id,
          data: file,
        }
        const res = await updateClaimAttachmentImages(body)
        if (res.error) {
          console.error('Error updating file:', res.error)
        } else {
          // Exit edit mode on successful update and reset to listing state
          setEditingFileIndex(null)
          onCancelAttachmentSet()
        }
        setUpdatingImageIndex(null)
      } catch (error) {
        // Check if it's a validation error
        if (error.inner) {
          // Handle validation errors
          const errors = {}
          error.inner.forEach(err => {
            errors[`file${index}.${err.path}`] = err.message
          })
          setFileValidationErrors(prev => ({ ...prev, ...errors }))
        } else {
          // Handle other errors
          console.error('Error updating file:', error)
        }
        setUpdatingImageIndex(null)
      }
    }
  }

  const handleCancelEdit = index => {
    if (addingFileIndex === index) {
      const updatedFiles = allAttachments.filter((_, i) => i !== index)
      setAllAttachments(updatedFiles)
      onChange({ ...values, images: updatedFiles })
      setAddingFileIndex(null)
    } else if (editingFileIndex === index) {
      setEditingFileIndex(null)
    } else {
      setEditingFileIndex(null)
      setAddingFileIndex(null)
    }
  }

  const handleRequestDeleteFile = fileIndex => {
    setFileToDelete(fileIndex)
    setShowFileDeleteConfirmation(true)
  }

  const handleConfirmDeleteFile = async () => {
    if (fileToDelete !== null) {
      await handleFileRemove(fileToDelete)
      setFileToDelete(null)
      setShowFileDeleteConfirmation(false)
    }
  }

  const handleCancelDeleteFile = () => {
    setFileToDelete(null)
    setShowFileDeleteConfirmation(false)
  }

  return (
    <Box>
      {/* Narrative Field - Always show when narrative exists or when editing */}
      {(values.narrative ||
        isEdit ||
        currentStep === 1 ||
        currentStep === 2 ||
        savedAttachmentId) && (
        <Box sx={{ mb: 2 }}>
          {isEdit && (
            <Typography
              variant="body2"
              sx={{ mb: 1, color: theme.palette.text.secondary }}
            >
              To improve the acceptance of a claim and provide context to an
              attachment, add a narrative.
            </Typography>
          )}
          <TextField
            fullWidth
            name="narrative"
            label="Narrative"
            placeholder="Describe the attachment..."
            value={values.narrative || ''}
            error={error[`${errorAffixe}.narrative`]}
            disabled={!isEdit || (isEdit && !isAdding) || currentStep === 2}
            onChange={value => onChange({ ...values, narrative: value })}
            outlineColor={
              isEdit ? theme.palette.secondary.main : theme.palette.primary.main
            }
            multiline
            rows={3}
          />
        </Box>
      )}

      {/* Files Section - Show in step 2 or when editing existing attachments */}
      {(currentStep === 2 ||
        savedAttachmentId ||
        allAttachments.length > 0 ||
        (!isAdding && isEdit)) && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
            Files ({allAttachments.length})
          </Typography>
          {/* File list/content */}
          {allAttachments.length === 0 && !isEdit && (
            <Box
              sx={{
                textAlign: 'center',
                py: 2,
                color: theme.palette.text.secondary,
              }}
            >
              <AttachFileIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2">No files attached</Typography>
            </Box>
          )}
          {allAttachments?.map((file, index) => (
            <FileRow
              key={file.id || index}
              isEditing={
                addingFileIndex === index || editingFileIndex === index
              }
              pendingUpload={file.pendingUpload}
              hasPendingSync={false}
              theme={theme}
            >
              <FileInfo>
                <AttachFileIcon sx={{ color: theme.palette.primary.main }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <FileName theme={theme}>
                    {file.name || file.filename || 'No File Name'}
                  </FileName>
                  {/* Image Status Indicator */}
                  {file.pendingUpload && (
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 0.5,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <StatusIndicator status="pending" theme={theme}>
                        <PendingIcon sx={{ fontSize: 12 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '9px',
                            fontWeight: 400,
                            color: theme.palette.warning[700] || '#f57c00',
                          }}
                        >
                          Pending Upload
                        </Typography>
                      </StatusIndicator>
                    </Box>
                  )}
                  {file.syncStatus && !file.pendingUpload && (
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 0.5,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <StatusIndicator status={file.syncStatus} theme={theme}>
                        {file.syncStatus === 'sync_completed' && (
                          <>
                            <SuccessIcon sx={{ fontSize: 12 }} />
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '9px',
                                fontWeight: 400,
                                color: theme.palette.success.dark,
                              }}
                            >
                              File Synced
                            </Typography>
                          </>
                        )}
                        {file.syncStatus === 'sync_failed' && (
                          <>
                            <ErrorIcon sx={{ fontSize: 12 }} />
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '9px',
                                fontWeight: 400,
                                color: theme.palette.error.dark,
                              }}
                            >
                              File Sync Failed
                            </Typography>
                          </>
                        )}
                      </StatusIndicator>
                    </Box>
                  )}
                </Box>
              </FileInfo>
              <Grid container spacing={1} sx={{ flex: 1, width: '100%' }}>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    label="Date"
                    type="date"
                    value={file.date || ''}
                    onChange={value => handleFileChange(index, 'date', value)}
                    disabled={
                      (addingFileIndex !== index &&
                        editingFileIndex !== index) ||
                      globalUploadingFiles.has(index)
                    }
                    size="small"
                    error={fileValidationErrors[`file${index}.date`]}
                    outlineColor={
                      isEdit
                        ? theme.palette.secondary.main
                        : theme.palette.primary.main
                    }
                  />
                </Grid>
                <Grid item xs={6} sm={5}>
                  <SelectField
                    fullWidth
                    name="typeCode"
                    label="Type"
                    placeholder="Type"
                    options={imageTypeCode}
                    value={file.typeCode}
                    onChange={value =>
                      handleFileChange(index, 'typeCode', value)
                    }
                    disabled={
                      (addingFileIndex !== index &&
                        editingFileIndex !== index) ||
                      globalUploadingFiles.has(index)
                    }
                    size="small"
                    error={fileValidationErrors[`file${index}.typeCode`]}
                    outlineColor={
                      isEdit
                        ? theme.palette.secondary.main
                        : theme.palette.primary.main
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <SelectField
                    fullWidth
                    name="orientationType"
                    label="Orientation"
                    placeholder="Orientation"
                    options={imageOrientations}
                    value={file.orientationType}
                    onChange={value =>
                      handleFileChange(index, 'orientationType', value)
                    }
                    disabled={
                      (addingFileIndex !== index &&
                        editingFileIndex !== index) ||
                      globalUploadingFiles.has(index)
                    }
                    size="small"
                    error={fileValidationErrors[`file${index}.orientationType`]}
                    outlineColor={
                      isEdit
                        ? theme.palette.secondary.main
                        : theme.palette.primary.main
                    }
                  />
                </Grid>
              </Grid>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexShrink: 0,
                  justifyContent: 'flex-end',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                {file.pendingUpload ? (
                  <>
                    {globalUploadingFiles.has(index) && (
                      <CircularProgress
                        size={20}
                        sx={{ margin: '4px' }}
                        color="secondary"
                      />
                    )}
                    <IconButton
                      onClick={() => handleConfirmFileUpload(index)}
                      disabled={globalUploadingFiles.has(index)}
                      size="small"
                    >
                      <CheckIcon color="secondary" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleCancelEdit(index)}
                      disabled={globalUploadingFiles.has(index)}
                      size="small"
                    >
                      <CancelOutlined color="error" />
                    </IconButton>
                  </>
                ) : addingFileIndex === index ? (
                  <>
                    {globalUploadingFiles.has(index) && (
                      <CircularProgress
                        size={20}
                        sx={{ margin: '4px' }}
                        color="secondary"
                      />
                    )}
                    <IconButton
                      onClick={() => handleCancelEdit(index)}
                      disabled={globalUploadingFiles.has(index)}
                      size="small"
                    >
                      <CancelOutlined color="error" />
                    </IconButton>
                  </>
                ) : editingFileIndex === index ? (
                  <>
                    <IconButton
                      onClick={() => handleSaveFile(index)}
                      disabled={
                        globalUploadingFiles.has(index) ||
                        updatingImageIndex === index
                      }
                      size="small"
                    >
                      {updatingImageIndex === index ? (
                        <CircularProgress size={16} color="secondary" />
                      ) : (
                        <CheckIcon color="secondary" />
                      )}
                    </IconButton>
                    <IconButton
                      onClick={() => handleCancelEdit(index)}
                      disabled={
                        globalUploadingFiles.has(index) ||
                        updatingImageIndex === index
                      }
                      size="small"
                    >
                      <CancelOutlined color="error" />
                    </IconButton>
                  </>
                ) : (
                  (allowFileEdit || currentStep === 2 || isAdding) &&
                  !viewOnly && (
                    <>
                      <IconButton
                        onClick={() => setEditingFileIndex(index)}
                        disabled={
                          globalUploadingFiles.has(index) ||
                          updatingImageIndex !== null ||
                          isDeletingImage
                        }
                        size="small"
                      >
                        <EditOutlined color="secondary" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleRequestDeleteFile(index)}
                        disabled={
                          globalUploadingFiles.has(index) ||
                          updatingImageIndex !== null ||
                          isDeletingImage
                        }
                        size="small"
                      >
                        {isDeletingImage ? (
                          <CircularProgress size={16} color="error" />
                        ) : (
                          <DeleteOutline color="error" />
                        )}
                      </IconButton>
                    </>
                  )
                )}
              </Box>
            </FileRow>
          ))}
          {isEdit && !viewOnly && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', sm: 'flex-end' },
                mt: 1,
              }}
            >
              <input
                type="file"
                onChange={e => handleFileAdd(e.target.files[0])}
                accept="*"
                style={{ display: 'none' }}
                id="file-upload"
              />
              <label htmlFor="file-upload" style={{ width: '100%' }}>
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<AttachFileIcon />}
                  disabled={globalUploadingFiles.size > 0}
                  fullWidth
                  sx={{ borderStyle: 'dashed' }}
                >
                  Add File
                </Button>
              </label>
            </Box>
          )}
        </Box>
      )}

      <Dialog
        open={showFileDeleteConfirmation}
        onClose={handleCancelDeleteFile}
        fullWidth
        maxWidth="xs"
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ marginBottom: '14px' }}>
            Are you sure you want to delete this file?
          </Typography>
          <Typography
            variant="body2"
            sx={{ marginBottom: '16px', color: theme.palette.text.secondary }}
          >
            This action cannot be undone.
          </Typography>
          <Box
            display="flex"
            width={'100%'}
            justifyContent={'flex-end'}
            gap={1}
          >
            <Button
              variant="text"
              color="secondary"
              onClick={handleCancelDeleteFile}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDeleteFile}
              sx={{ color: '#fff' }}
              disabled={
                isLoadingCreate ||
                globalUploadingFiles.size > 0 ||
                isDeletingImage
              }
              startIcon={
                isDeletingImage ? (
                  <CircularProgress size={14} color="inherit" />
                ) : null
              }
            >
              {isDeletingImage ? 'Deleting...' : 'Delete'}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  )
}

export default Attachments
